import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/modal-drawer.html
 *
 * Radius 10px (radii.modal), shadow.lg, overlay rgba(24,36,60,0.86) (drawn
 * at half strength here, see `backdrop`).
 * Header padding 16px 18px 12px, H3 15px semibold, body 13px.
 * Footer 12px 18px with top border-subtle, right-aligned actions.
 *
 * v3 port notes (v2's Modal is renamed to Dialog):
 * - Slots renamed from v2's `modalAnatomy`: `overlay` → `backdrop`, `dialog` →
 *   `content`, `dialogContainer` → `positioner`, `closeButton` →
 *   `closeTrigger`. `header`/`body`/`footer` are unchanged names.
 * - Chakra's built-in `dialog` recipe deep-merges *underneath* this one, same
 *   mechanism as Card/Tabs/Menu — confirmed empirically via a throwaway
 *   `createSystem`, not by reading the built-in's source alone. Its `size`
 *   variant bleeds through like Menu's does, and the two versions map size
 *   names differently: v2's stock Modal used the same-named `sizes` token
 *   (`md` → maxW `md`, 28rem), while v3's built-in steps one up (`xs` → `sm`,
 *   `sm` → `md`, `md` → `lg`, `lg` → `2xl`), so every dialog came out one
 *   size wider (MCenterModal's default `lg` went from 32rem to 42rem). The
 *   `xs`–`lg` entries below restore v2's mapping. `xl`/`full` are v2's own
 *   customizations, and `2xl`–`6xl` are v2 stock sizes that v3's built-in
 *   `dialog` recipe doesn't define at all.
 * - `placement`, `scrollBehavior`, `size`, and `motionPreset` are now recipe
 *   *variants* (threaded onto `Dialog.Root` via Chakra's `SlotRecipeProps`),
 *   not behavioral props on Ark's dialog machine — confirmed in
 *   `@zag-js/dialog`'s real `DialogProps`, which has none of these. The
 *   built-in recipe already implements sensible `placement`
 *   (center/top/bottom) and `scrollBehavior` (inside/outside) variants
 *   matching v2's `isCentered`/`scrollBehavior` semantics, so this file
 *   doesn't redefine either — `MCenterModal.tsx` translates v2's boolean
 *   `isCentered` prop to the `placement` variant instead.
 * - `motionPreset`'s variant *values* are dash-case in v3
 *   (`slide-in-bottom`, not v2's `slideInBottom`) — translated in
 *   `MCenterModal.tsx`, not here, so this recipe doesn't need to redefine
 *   the built-in's already-correct animations either.
 * - `slots` below must list every anatomy part in the *same order* as the
 *   built-in recipe (`trigger, backdrop, positioner, content, title,
 *   description, closeTrigger, header, body, footer`), even the ones this
 *   recipe never styles. Chakra's config merge (`mergeConfigs`) merges
 *   array fields positionally by index, not by value — a shorter/reordered
 *   `slots` array here silently overwrites built-in slot *names* at those
 *   indices in the merged theme. A previous version of this list only had
 *   6 entries and, purely by index collision, clobbered `positioner` out of
 *   the merged anatomy entirely: `Dialog.Positioner` rendered with no class
 *   and no styles at all, so every `placement="center"` dialog collapsed to
 *   the top of the viewport instead of centering (caught via
 *   `AppTenantSwitchModal`, but affected any consumer of `MCenterModal`
 *   too, since its default `isCentered` maps to the same broken variant).
 */

export const dialogTheme = defineSlotRecipe({
  slots: [
    'trigger',
    'backdrop',
    'positioner',
    'content',
    'title',
    'description',
    'closeTrigger',
    'header',
    'body',
    'footer',
  ],
  base: {
    backdrop: {
      // Half-strength overlay, the look every dialog had via an inline
      // `opacity: 0.5`. It lives in the color instead: v3 fades the backdrop in
      // with a CSS `opacity` animation, which overrides inline styles, so the
      // backdrop ramped up to full strength and then snapped back to half.
      bg: 'bg.overlay/50',
    },
    // An open modal dialog sets `pointer-events: none` on <body> and turns
    // them back on only for the content, so the full-screen positioner (and
    // the backdrop under it) inherited `none`. A drag starting beside a modal
    // that stays open on outside clicks then selected the page behind it.
    // v2's container caught those pointers. zag still sets an inline `none`
    // while closed and on non-modal dialogs, which beats this class.
    positioner: {
      pointerEvents: 'auto',
    },
    content: {
      borderRadius: 'modal',
      boxShadow: 'lg',
      bg: 'bg.surface',
    },
    header: {
      px: '18px',
      pt: 4,
      pb: 3,
      fontSize: 'xl', // 16px — close to design system's 15px H3
      fontWeight: 600,
      color: 'fg.strong',
      letterSpacing: 'tight',
    },
    body: {
      pb: 4,
      px: '18px',
      fontSize: 'sm',
      color: 'fg.primary',
    },
    footer: {
      pt: 3,
      px: '18px',
      pb: 3,
      borderTop: '1px solid',
      borderColor: 'border.subtle',
    },
    closeTrigger: {
      top: 3,
      insetEnd: 3,
      fontSize: '12px',
      borderRadius: 'sm',
      // v3's preflight no longer gives buttons `cursor: pointer`.
      cursor: 'pointer',
      _hover: { bg: 'bg.hover' },
    },
  },
  variants: {
    size: {
      // v2's stock Modal mapping (size name → same-named `sizes` token). The
      // built-in recipe maps each of these one token wider.
      xs: { content: { maxW: 'xs' } },
      sm: { content: { maxW: 'sm' } },
      md: { content: { maxW: 'md' } },
      lg: { content: { maxW: 'lg' } },
      // v2's Modal.ts customized these two away from Chakra's stock Modal
      // sizes; kept as-is here.
      xl: {
        content: {
          maxW: '80vw',
          width: '800px',
        },
      },
      full: {
        content: {
          minH: '95vh',
          maxH: '95vh',
          minW: '95vw',
          maxW: '95vw',
          // The built-in `full` size is edge-to-edge (100dvw) and sets
          // `borderRadius: 0`, which bleeds through and squares off ours.
          borderRadius: 'modal',
        },
      },
      // Not in v3's built-in `size` variant at all (only goes up to `xl` +
      // `cover`/`full`) — v2's stock Chakra Modal sizes included these, and
      // this app uses them, so they're added as plain new options.
      '2xl': { content: { maxW: '2xl' } },
      '3xl': { content: { maxW: '3xl' } },
      '4xl': { content: { maxW: '4xl' } },
      '5xl': { content: { maxW: '5xl' } },
      '6xl': { content: { maxW: '6xl' } },
    },
  },
});

export default dialogTheme;
